import React from 'react';
import {memoize} from '@packages/utilities/memo';
import {
  CHECKOUT_STATES,
  BILLING_ADDRESS_TOGGLE_DEFAULT,
  DATA_ATTR_NODE_TYPE,
  NODE_TYPE_COMMERCE_CHECKOUT_FORM_CONTAINER,
  CHECKOUT_BINDING_ROOT_QUERY_PATH,
  ORDER_TYPE,
  CHECKOUT_QUERY,
  PREVIEW_ITEMS_DEFAULT,
  BILLING_ADDRESS_TOGGLE_KEY,
  PREVIEW_ITEMS_KEY,
} from '@packages/systems/commerce/constants';
import {getPaddedUserItemsAndTotals, UserItem} from './utils/OrderItemsUtils';
import {
  withMeta,
  withSystem,
  ElementPreset,
  Plugins,
  Atom,
  ContextType,
} from '@packages/systems/wfdl';
import {iconName, typeName, qualify} from '@packages/systems/wfdl-Qualified';
import {px, percent, vh} from '@packages/systems/core/utils/WebflowCssSubset';
import {createRgbaColor} from '@packages/systems/core/utils/ColorUtils';
import {buildCheckoutForm} from './CommerceUtils';
import {getNewElementId} from '@packages/utilities/platform';
import {
  SHARED_FORM_WRAPPER_CONSTRAINTS_DESCENDANTS,
  SHARED_FORM_CONSTRAINTS_DESCENDANTS,
  SHARED_DYN_WRAPPER_CONSTRAINTS_ANCESTORS,
  SHARED_DYN_WRAPPER_CONSTRAINTS_DESCENDANTS,
} from '@packages/systems/core/constants/SharedConstraintRules';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import withAttribute from './withAttribute';
import {Eye} from '@packages/systems/ui/icons';

import {
  TListOfElement,
  TRecord,
  TText,
  TEnum,
  TTypeReference,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {useRuntimeEnvironment, useSite} from '@packages/systems/render';
import {
  EvaluatedNodeData,
  getBooleanOrElse,
  getChildren,
  getInPojo,
  getNumberOrElse,
  getString,
} from '@packages/utilities/data-utils';
import {
  CurrencySettings,
  FormattedPrice,
} from '@packages/systems/core/utils/CurrencyUtils';

const setRequiresShipping =
  (value: boolean) => (contextData: Record<string, unknown>) => ({
    ...contextData,
    requiresShipping: value,
  });

const memoizedGetUserItemsAndTotals = memoize(
  (
    previewItemsCount: number,
    userItems: UserItem[],
    extraItems: {price: FormattedPrice}[],
    currencySettings: CurrencySettings
  ) =>
    getPaddedUserItemsAndTotals(
      previewItemsCount,
      userItems,
      extraItems,
      currencySettings
    )
);

const CommerceCheckoutFormContainer = Atom.createRender(
  ({attributes, classes, context, data, generateAttributes, renderChild}) => {
    const site = useSite();
    const runtimeEnvironment = useRuntimeEnvironment();

    // Slight hack to overwrite mock order requiresShipping context to use toggle on form.
    const isDesigner = runtimeEnvironment === WebflowEnvironments.DESIGNER;
    const mockRequiresShipping =
      (getString(getInPojo(data, ['temp', 'requiresShipping'])) ??
        ORDER_TYPE.REQUIRE_SHIPPING) === ORDER_TYPE.REQUIRE_SHIPPING;

    const previewItemsCount = getNumberOrElse(
      getInPojo(data, ['temp', PREVIEW_ITEMS_KEY]),
      PREVIEW_ITEMS_DEFAULT
    );
    // TODO (ecommerce): Memoize commerce context mutations
    const newContext: ContextType = context
      .update('data', (contextData) => {
        if (isDesigner) {
          const dataWithRequiresShipping = setRequiresShipping(
            mockRequiresShipping
          )(contextData || {});

          const currencySettings = context.get('ecommCurrencySettings');

          if (currencySettings && contextData) {
            const extraItems = contextData.extraItems || [];
            const userItems = contextData.userItems || [];
            const {paddedUserItems, subtotal, total} =
              memoizedGetUserItemsAndTotals(
                previewItemsCount,
                userItems,
                extraItems,
                currencySettings
              );
            return {
              ...dataWithRequiresShipping,
              userItems: paddedUserItems,
              subtotal,
              total,
            };
          }
          return dataWithRequiresShipping;
        }
        return contextData;
      })
      .set(
        'tempState',
        getString(getInPojo(data, ['temp', 'state'])) ?? CHECKOUT_STATES.DEFAULT
      )
      .set('bindingPathPrefix', CHECKOUT_BINDING_ROOT_QUERY_PATH)
      // get Billing toggle state from the node.data.temp, and set it in context so it is available to descendants
      .set(
        'isBillingAddressOpen',
        getBooleanOrElse(
          getInPojo(data, ['temp', BILLING_ADDRESS_TOGGLE_KEY]),
          BILLING_ADDRESS_TOGGLE_DEFAULT
        )
      )
      .set('previewItemsCount', previewItemsCount);

    const query = context.get('query') || '';
    const newAttributes = attributes
      .set(CHECKOUT_QUERY, query)
      .set('data-wf-page-link-href-prefix', site.get('pageLinkHrefPrefix', ''));

    return (
      <div {...generateAttributes(newAttributes, classes)}>
        {getChildren(data).map((child: EvaluatedNodeData) =>
          renderChild(child, newContext)
        )}
      </div>
    );
  }
);

export const createCommerceCheckoutFormContainer: ElementPreset.ExpressionEscapeHatchFactory =
  ({collectionState, componentMap, betaFeatures}) => {
    if (componentMap === undefined || collectionState === undefined) {
      throw Error(
        'componentMap and collectionState are required to create CommerceCheckoutFormContainer'
      );
    }
    const billingAddressToggleId = getNewElementId(componentMap, 'checkbox');

    return buildCheckoutForm({
      billingAddressToggleId,
      collectionState,
      betaFeatures: betaFeatures || null,
    });
  };

const config = createIntegratedAtom({
  displayName: 'Checkout Form',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Form'), iconName('FormBlock')),
  },
  showEmpty: true,
  constraints: {
    descendants: [
      ...SHARED_FORM_WRAPPER_CONSTRAINTS_DESCENDANTS,
      ...SHARED_DYN_WRAPPER_CONSTRAINTS_DESCENDANTS,
      ...SHARED_FORM_CONSTRAINTS_DESCENDANTS,
      {is: 'CommerceCheckoutBillingAddressToggleWrapper', rule: 'ExactlyOne'},
      {is: 'CommerceCheckoutCustomerInfoWrapper', rule: 'ExactlyOne'},
      {is: 'CommerceCheckoutPaymentInfoWrapper', rule: 'ExactlyOne'},
      {is: 'CommerceCheckoutPlaceOrderButton', rule: 'ExactlyOne'},
      {is: 'CommerceCheckoutShippingAddressWrapper', rule: 'ExactlyOne'},
      {is: 'CommerceCheckoutShippingMethodsWrapper', rule: 'ExactlyOne'},
      {is: 'CommerceCheckoutErrorState', rule: 'ExactlyOne'},
      {is: 'CommerceCheckoutBillingAddressWrapper', rule: 'ExactlyOne'},
      {is: 'CommerceCheckoutAdditionalInputsContainer', rule: 'ZeroOrOne'},
      {is: 'CommerceCheckoutDiscounts', rule: 'ZeroOrOne'},
    ],
    ancestors: SHARED_DYN_WRAPPER_CONSTRAINTS_ANCESTORS,
    page: [{type: 'UtilityPage', is: 'ecommerce-checkout'}],
  },
  style: {
    base: {
      main: {
        noPseudo: {
          width: percent(100),
          minHeight: vh(100),
          paddingTop: px(20),
          paddingBottom: px(20),
          paddingRight: px(20),
          paddingLeft: px(20),
          backgroundColor: createRgbaColor('f5f5f5'),
        },
      },
    },
  },

  render: withAttribute(
    DATA_ATTR_NODE_TYPE,
    NODE_TYPE_COMMERCE_CHECKOUT_FORM_CONTAINER
  )(CommerceCheckoutFormContainer),
  fields: {
    commerce: withSystem(
      {private: true},
      TRecord({
        type: TText,
      })
    ),
    temp: TRecord(
      {
        requiresShipping: withSystem(
          {
            label: 'Order',
            defaultValue: ORDER_TYPE.REQUIRE_SHIPPING,
          },
          TEnum({
            [ORDER_TYPE.REQUIRE_SHIPPING]: 'Shipping',
            [ORDER_TYPE.NO_SHIPPING]: 'No Shipping',
          })
        ),
        state: withSystem(
          {
            label: 'State',
            defaultValue: CHECKOUT_STATES.DEFAULT,
          },
          TEnum({
            [CHECKOUT_STATES.DEFAULT]: 'Default',
            [CHECKOUT_STATES.ERROR]: 'Error',
          })
        ),
        previewItems: withSystem(
          {
            label: 'Preview',
            defaultValue: PREVIEW_ITEMS_DEFAULT,
          },
          TTypeConstructor(qualify(Plugins.name('Basic'), typeName('Slider')))
        ),
        note: withMeta(
          {
            content:
              'You can preview up to 20 order items. If you do not have enough products, sample items will be generated.',
          },
          withSystem(
            {defaultValue: false},
            TTypeConstructor(
              qualify(Plugins.name('Commerce'), typeName('DismissibleNote'))
            )
          )
        ),
        [BILLING_ADDRESS_TOGGLE_KEY]: withMeta(
          {
            secondaryLabel: 'Hide Billing Address',
            icon: Eye,
          },
          withSystem(
            {
              label: 'Show Billing Address',
              defaultValue: BILLING_ADDRESS_TOGGLE_DEFAULT,
              labelVariant: 'hidden',
            },
            TTypeConstructor(
              qualify(Plugins.name('Basic'), typeName('ToggleButton'))
            )
          )
        ),
      },
      TTypeReference('extraTempData')
    ),
    children: TListOfElement,
  },
});

export default config;
